/**
 Copyright Highway9 Networks Inc.
 */
import moment from "moment";
import { authenticationService } from "./authentication-service";
import { groupBy } from "~/helpers/utils";
import { downloadBlobFile } from "~/helpers/downloadFile";
import APIService from "./APIServices";
import { Input, OperationDetails } from "~/types/operations";

const operationAPI = new APIService<OperationDetails>("operations");

export function getSessionInitiatedOperations(): string[] {
  try {
    const existing = sessionStorage.getItem(SESSION_OPERATIONS_KEY);
    return existing ? JSON.parse(existing) : [];
  } catch (error) {
    console.error('Failed to read session operations:', error);
    return [];
  }
}

export function setSessionInitiatedOperations(operationIds: string[]) {
  sessionStorage.setItem(SESSION_OPERATIONS_KEY, JSON.stringify(operationIds));
}
// Track operations created in the current session for auto-download limitation
export const SESSION_OPERATIONS_KEY = 'sessionInitiatedOperations';

function trackOperationInSession(operationId: string) {
  try {
    const existing = getSessionInitiatedOperations();
    if (!existing.includes(operationId)) {
      existing.push(operationId);
      setSessionInitiatedOperations(existing);
    }
  } catch (error) {
    console.error('Failed to track operation in session:', error);
  }
}

async function createOperationWithTracking(payload: OperationDetails) {
  const result = await operationAPI.create(payload);
  // Track the operation ID if it exists
  if (result?.id) {
    trackOperationInSession(result.id);
  }
  return result;
}

export const operationService = {
  createOperation: createOperationWithTracking,
  cancelOperation,
  deleteOperation: operationAPI.delete,
  updateOperation,
  GetOperationById,
  GenerateTechBundle,
  GenerateBulkDownload,
  GeneratePcap,
  DownloadFile,
  GetBundleStatus,
  GetPcapStatus,
  GetExportStatus,
  GetRadioMonitoringStatus,
  getAllOperations,
  getScheduledClash,
  StartRadioMonitoring
};

async function updateOperation(payload: OperationDetails, id: string) {
  try {
    delete payload.id;
    delete payload.status;
    delete payload.statusMessage;
    const response = await operationAPI.update(payload, id);
    return response;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

async function GetOperationById(id: string) {
  try {
    return await operationAPI.get(id);
  } catch (error) {
    console.log(error);
    throw error;
  }
}

async function cancelOperation(id: string) {
  try {
    const response = await operationAPI.post("cancel/" + id);
    return response;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * @example
 * DownloadFile("https://www.example.com/file.pdf")
 */
export async function DownloadFile(link: string) {
  const _path = new URL(link).pathname;
  const url = location.origin + _path;
  const requestOptions = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      accept: "*/*",
      Authorization: "Bearer " + authenticationService.currentUserValue?.token,
    },
  };
  try {
    const response = await fetch(url, requestOptions);

    const filenameHeader = response.headers.get("content-disposition") ?? "";
    const subStrings = filenameHeader.split("=");
    const filename = subStrings[1].trim().replace(/"/g, "");

    const blob = await response.blob();
    downloadBlobFile(blob, filename);
    return true;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

type OQuery = {
  objectType?: string;
  objectId?: string;
  operationType?: string;
  startTime?: number;
  endTime?: number;
  status?: string;
  input?: Input[]
};

async function getAllOperations(query: OQuery) {
  try {
    const operations = (await operationAPI.post("query", query)) as unknown as OperationDetails[];
    return operations;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

async function getScheduledClash(query: OQuery) {
  try {
    const response = await operationAPI.post('query/scheduleClash', query);
    return response as unknown as OperationDetails[];
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Generate a tech support bundle for the given edge ID
  @example
  GenerateTechBundle("edge-1", {
    systemInfo: true,
    logFiles: true,
    coreDumps: true,
  }
 */
async function GenerateTechBundle(
  edgeID: string,
  params: {
    systemInfo: string;
    logFiles: string;
    coreDumps: string;
    packetCapture: string;
    startTime: string;
    endTime: string;
  }
) {
  const requestBody = {
    name: "Tech_bundle_" + Date.now(),
    type: "operation",
    operationType: "GENERATE_TECH_SUPPORT_BUNDLE",
    objectId: edgeID,
    objectType: "edge",
    input: [
      { key: "systemInfo", value: params.systemInfo },
      { key: "logFiles", value: params.logFiles },
      { key: "coreDumps", value: params.coreDumps },
      { key: "startTime", value: params.startTime },
      { key: "packetCapture", value: params.packetCapture },
      { key: "endTime", value: params.endTime },
    ],
  };

  try {
    const data = await operationService.createOperation(requestBody);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

function GeneratePcap(
  name: string,
  edgeID: string,
  params?: {
    capture5GControlPlanePackets?: boolean;
    capture5GDataPlanePackets?: boolean;
    filter: string;
    dataPlaneFilter?: string;
    fileSize: string;
    timeout: string;
    filename: string;
    edgeInterface: string;
    edgeDataInterface?: string;
    controlPlaneIp?: string;
  }
) {
  const requestBody: OperationDetails = {
    name: name,
    type: "operation",
    operationType: name,
    objectId: edgeID,
    objectType: "edge",
  };

  if (params) {
    requestBody.input = [
      { key: "filter", value: params.filter },
      { key: "fileSize", value: params.fileSize },
      { key: "timeout", value: params.timeout },
      { key: "filename", value: params.filename },
      { key: "interface", value: params.edgeInterface },
    ];
    if ("capture5GControlPlanePackets" in params || "capture5GDataPlanePackets" in params) {
      requestBody?.input?.push(
        { key: "capture5GControlPlanePackets", value: params.capture5GControlPlanePackets ? "true" : "false" },
        { key: "capture5GDataPlanePackets", value: params.capture5GDataPlanePackets ? "true" : "false" },
        { key: "dataPlaneFilter", value: params.dataPlaneFilter ?? null },
        { key: "edgeDataInterface", value: params.edgeDataInterface ?? null },
        { key: "controlPlaneIp", value: params.controlPlaneIp ?? null }
      );
    }
  }
  return operationService.createOperation(requestBody);
}

async function GenerateBulkDownload(operationType: string, objectType: string) {
  const requestBody = {
    name: operationType,
    type: "operation",
    operationType: operationType,
    objectType: objectType,
    isBulk: true,
  };

  try {
    const data = await operationService.createOperation(requestBody);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * @example
 * GetExportStatus("radio", "EXPORT")
 */
async function GetExportStatus(objectType: string, operationType: string) {
  type Group = "ACKNOWLEDGED" | "IN_PROGRESS" | "FAILED" | "COMPLETED" | "CANCELLED";
  const operations = await getAllOperations({ operationType, objectType });
  const data = groupBy(operations, "status");

  const result: Record<Group, OperationDetails[]> = {
    ACKNOWLEDGED: [],
    IN_PROGRESS: [],
    FAILED: [],
    COMPLETED: [],
    CANCELLED: [],
    ...data,
  };
  return result;
}

async function GetBundleStatus(edgeID: string) {
  const requestBody = {
    objectType: "edge",
    objectId: edgeID,
    operationType: "GENERATE_TECH_SUPPORT_BUNDLE",
    startTime: moment().subtract(1, "days").unix(),
    endTime: moment().unix(),
  };

  type Group = "ACKNOWLEDGED" | "IN_PROGRESS" | "FAILED" | "COMPLETED" | "CANCELLED";
  const operations = await getAllOperations(requestBody);
  const data = groupBy(operations, "status");

  const result: Record<Group, OperationDetails[]> = {
    ACKNOWLEDGED: [],
    IN_PROGRESS: [],
    FAILED: [],
    COMPLETED: [],
    CANCELLED: [],
    ...data,
  };
  return result;
}

async function GetPcapStatus(edgeID: string, name = "START_PACKET_CAPTURE") {
  const requestBody = {
    objectType: "edge",
    objectId: edgeID,
    operationType: name,
    startTime: moment().subtract(1, "days").unix(),
    endTime: moment().unix(),
  };

  type Group = "ACKNOWLEDGED" | "IN_PROGRESS" | "FAILED" | "COMPLETED" | "CANCELLED" | "FETCHING_RESULTS" | "WAIT_NOT_STARTED" | "WAITING_IN_PROGRESS" | "raw";
  const operations = await getAllOperations(requestBody);
  const data = groupBy(operations, "status");

  const result: Record<Group, OperationDetails[]> = {
    ACKNOWLEDGED: [],
    IN_PROGRESS: [],
    FAILED: [],
    COMPLETED: [],
    CANCELLED: [],
    FETCHING_RESULTS: [],
    WAIT_NOT_STARTED: [],
    WAITING_IN_PROGRESS: [],
    ...data,
    raw: operations,
  };
  return result;
}

async function GetRadioMonitoringStatus(radioID: string) {
  return getAllOperations({
    objectType: "radio",
    objectId: radioID,
    operationType: "START_RADIO_MONITORING",
  });
}

async function StartRadioMonitoring(
  radioID: string,
  params: {
    logFiles?: Record<string, any>; // changed from string[] to object
    packetCapture?: { source: string[] };
    radioManagerDiagnostics?: object;
    startTime: string;
    endTime: string;
    timeout: string;
    opName?: string;
  }
) {
  const requestBody = {
    name: params.opName || ("Radio_Monitoring_" + new Date().toISOString()),
    type: "operation",
    operationType: "START_RADIO_MONITORING",
    objectId: radioID,
    objectType: "radio",
    input: [
      { key: "logFiles", value: params.logFiles ?? {} }, // now expects an object
      { key: "packetCapture", value: params.packetCapture ?? {} },
      { key: "radioManagerDiagnostics", value: params.radioManagerDiagnostics ?? {} },
      { key: "startTime", value: params.startTime ?? null },
      { key: "endTime", value: params.endTime ?? null },
      { key: "timeout", value: params.timeout ?? null },
      { key: "name", value: params.opName ?? null }
    ]
  };

  try {
    return await operationService.createOperation(requestBody);
  } catch (error) {
    console.error(error);
    throw error;
  }
}
